import React, { useState, useEffect, useRef } from 'react';

// ─── Constants & Mock Data ──────────────────────────────────────────

const PILLAR_ONE_CYCLE = 5000;
const PILLAR_TWO_CYCLE = 6000;
const PILLAR_THREE_CYCLE = 5500;

const FEED_COMMENTS = [
    'Love this aesthetic!',
    'Your creativity is inspiring',
    'This made my day better',
];
const FOLLOWER_COUNTS = [1247, 1248, 1250, 1253];

// ─── Pillar 1: Heart-to-Comment Morph ───────────────────────────────

const PillarOneAnimation: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    // Phases: 0=idle, 1=heart pop, 2=heart shake, 3=dissolve/crossfade, 4=comment card slide up, 5=checkmark
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        if (!isVisible) { setPhase(0); return; }
        const run = () => {
            setPhase(1);
            setTimeout(() => setPhase(2), 700);
            setTimeout(() => setPhase(3), 1400);
            setTimeout(() => setPhase(4), 2200);
            setTimeout(() => setPhase(5), 3400);
            setTimeout(() => setPhase(0), PILLAR_ONE_CYCLE);
        };
        run();
        const id = setInterval(run, PILLAR_ONE_CYCLE);
        return () => clearInterval(id);
    }, [isVisible]);

    return (
        <div style={{ width: '100%', height: 260, position: 'relative', overflow: 'hidden' }}>
            {/* Mini post card */}
            <div style={{
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 12,
                padding: '16px 20px',
                margin: '0 auto',
                maxWidth: 280,
            }}>
                {/* Post header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                    }} />
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>@creative_soul</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Fashion Post</div>
                    </div>
                </div>

                {/* Post image placeholder */}
                <div style={{
                    width: '100%', height: 80, borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    marginBottom: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21,15 16,10 5,21" />
                    </svg>
                </div>

                {/* Action row: heart + comment icons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {/* Heart icon */}
                    <div style={{
                        position: 'relative',
                        width: 28, height: 28,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: phase >= 3 ? 0 : 1,
                        transition: 'opacity 0.4s ease',
                    }}>
                        <svg
                            width="24" height="24" viewBox="0 0 24 24"
                            fill={phase >= 1 ? '#EF4444' : 'none'}
                            stroke={phase >= 1 ? '#EF4444' : 'rgba(255,255,255,0.6)'}
                            strokeWidth="2"
                            className={phase === 1 ? 'mission-heart-pop' : phase === 2 ? 'mission-heart-shake' : ''}
                        >
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                    </div>

                    {/* Comment icon */}
                    <div style={{
                        opacity: phase >= 3 ? 1 : 0.6,
                        transition: 'opacity 0.4s ease',
                        transform: phase >= 3 ? 'scale(1.15)' : 'scale(1)',
                    }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Comment card slides up */}
            <div style={{
                position: 'absolute',
                bottom: phase >= 4 ? 0 : -80,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 260,
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 10,
                padding: '12px 16px',
                transition: 'bottom 0.5s cubic-bezier(0.22,1,0.36,1)',
                backdropFilter: 'blur(8px)',
            }}>
                <div style={{
                    fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4,
                    display: 'flex', alignItems: 'center', gap: 6,
                }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    Your comment
                </div>
                <div style={{
                    fontSize: 13, color: '#fff', lineHeight: 1.5,
                    opacity: phase >= 4 ? 1 : 0,
                    transition: 'opacity 0.6s ease 0.2s',
                }}>
                    "Love how you styled this! The color combo is perfect"
                </div>
                {/* Checkmark */}
                <div style={{
                    position: 'absolute', top: 10, right: 12,
                    opacity: phase >= 5 ? 1 : 0,
                    transform: phase >= 5 ? 'scale(1)' : 'scale(0.5)',
                    transition: 'all 0.3s ease',
                }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3">
                        <polyline points="20,6 9,17 4,12" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

// ─── Pillar 2: Engagement Ripple Feed ───────────────────────────────

const PillarTwoAnimation: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    // Phases: 0=idle, 1=comment1, 2=comment2, 3=comment3, 4=glow+label
    const [phase, setPhase] = useState(0);
    const [followerIdx, setFollowerIdx] = useState(0);

    useEffect(() => {
        if (!isVisible) { setPhase(0); setFollowerIdx(0); return; }
        const run = () => {
            setFollowerIdx(0); setPhase(0);
            setTimeout(() => { setPhase(1); setFollowerIdx(1); }, 600);
            setTimeout(() => { setPhase(2); setFollowerIdx(2); }, 1800);
            setTimeout(() => { setPhase(3); setFollowerIdx(3); }, 3000);
            setTimeout(() => setPhase(4), 4200);
            setTimeout(() => { setPhase(0); setFollowerIdx(0); }, PILLAR_TWO_CYCLE);
        };
        run();
        const id = setInterval(run, PILLAR_TWO_CYCLE);
        return () => clearInterval(id);
    }, [isVisible]);

    const posts = ['Sunset Photo', 'Coffee Art', 'Street Style'];

    return (
        <div style={{ width: '100%', height: 280, position: 'relative', overflow: 'hidden' }}>
            {/* Follower counter */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, marginBottom: 16,
            }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span style={{
                    fontSize: 18, fontWeight: 700, color: '#fff',
                    fontVariantNumeric: 'tabular-nums',
                    transition: 'color 0.3s ease',
                    ...(phase === 4 ? { color: '#10B981' } : {}),
                }}>
                    {FOLLOWER_COUNTS[followerIdx].toLocaleString()}
                </span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>followers</span>
            </div>

            {/* Mini feed */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {posts.map((post, idx) => {
                    const hasComment = phase > idx;
                    return (
                        <div key={idx} style={{
                            position: 'relative',
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: 'rgba(255,255,255,0.08)',
                            borderRadius: 10, padding: '10px 14px',
                            transition: 'box-shadow 0.4s ease',
                            ...(hasComment && phase < 4 ? { boxShadow: '0 0 0 2px rgba(124,58,237,0.4)' } : {}),
                            ...(phase === 4 ? { boxShadow: '0 0 12px rgba(124,58,237,0.3)' } : {}),
                        }}>
                            {/* Post thumbnail */}
                            <div style={{
                                width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                                background: `linear-gradient(135deg, ${['#f093fb,#f5576c', '#667eea,#764ba2', '#43e97b,#38f9d7'][idx]})`,
                            }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>{post}</div>
                                {/* Comment slides in */}
                                <div style={{
                                    fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4,
                                    maxHeight: hasComment ? 30 : 0,
                                    opacity: hasComment ? 1 : 0,
                                    overflow: 'hidden',
                                    transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
                                    marginTop: hasComment ? 2 : 0,
                                }}>
                                    {FEED_COMMENTS[idx]}
                                </div>
                            </div>
                            {/* Ripple rings */}
                            {hasComment && phase <= 3 && (
                                <div className="mission-ripple" style={{
                                    position: 'absolute', right: 14, top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: '#7C3AED',
                                }} />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* +6 new followers label */}
            <div style={{
                textAlign: 'center', marginTop: 12,
                opacity: phase === 4 ? 1 : 0,
                transform: phase === 4 ? 'translateY(0)' : 'translateY(8px)',
                transition: 'all 0.5s ease',
            }}>
                <span style={{
                    display: 'inline-block',
                    fontSize: 12, fontWeight: 600,
                    color: '#10B981',
                    background: 'rgba(16,185,129,0.15)',
                    padding: '4px 12px', borderRadius: 100,
                }}>
                    +6 new followers today
                </span>
            </div>
        </div>
    );
};

// ─── Pillar 3: Approval Gate ────────────────────────────────────────

const PillarThreeAnimation: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    // Phases: 0=idle, 1=AI sparkle + card materialize, 2=card travels to gate, 3=scan, 4=tap approve, 5=posted celebration
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        if (!isVisible) { setPhase(0); return; }
        const run = () => {
            setPhase(1);
            setTimeout(() => setPhase(2), 900);
            setTimeout(() => setPhase(3), 1800);
            setTimeout(() => setPhase(4), 2700);
            setTimeout(() => setPhase(5), 3600);
            setTimeout(() => setPhase(0), PILLAR_THREE_CYCLE);
        };
        run();
        const id = setInterval(run, PILLAR_THREE_CYCLE);
        return () => clearInterval(id);
    }, [isVisible]);

    // Card X position based on phase
    const cardLeft = phase === 0 ? '8%' : phase === 1 ? '8%' : phase <= 3 ? '38%' : phase === 4 ? '38%' : '72%';

    return (
        <div style={{ width: '100%', height: 260, position: 'relative', overflow: 'hidden' }}>
            {/* Three columns: AI | Gate | Posted */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0 8px', marginBottom: 20 }}>
                {/* AI icon */}
                <div style={{ textAlign: 'center', flex: '0 0 70px' }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: 'rgba(124,58,237,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 6px',
                        ...(phase === 1 ? { animation: 'mission-ai-sparkle 0.6s ease' } : {}),
                    }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="2">
                            <path d="M12 2L9 9l-7 3 7 3 3 7 3-7 7-3-7-3z" />
                        </svg>
                    </div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Draft</div>
                </div>

                {/* Gate/Shield icon */}
                <div style={{ textAlign: 'center', flex: '0 0 70px' }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: phase >= 4 ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 6px',
                        transition: 'background 0.4s ease',
                    }}>
                        {phase >= 4 ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                                <polyline points="20,6 9,17 4,12" />
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                        )}
                    </div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {phase >= 4 ? 'Approved' : 'Your Review'}
                    </div>
                </div>

                {/* Posted icon */}
                <div style={{ textAlign: 'center', flex: '0 0 70px' }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: phase === 5 ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 6px',
                        transition: 'background 0.4s ease',
                        ...(phase === 5 ? { animation: 'mission-celebration 0.5s ease' } : {}),
                    }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={phase === 5 ? '#10B981' : 'rgba(255,255,255,0.3)'} strokeWidth="2">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                    </div>
                    <div style={{ fontSize: 9, color: phase === 5 ? '#10B981' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', transition: 'color 0.3s' }}>
                        {phase === 5 ? 'Posted!' : 'Posted'}
                    </div>
                </div>
            </div>

            {/* Connector line */}
            <div style={{
                position: 'absolute', top: 60, left: '18%', right: '18%',
                height: 2, background: 'rgba(255,255,255,0.1)', borderRadius: 1,
            }}>
                {/* Progress indicator */}
                <div style={{
                    height: '100%', borderRadius: 1,
                    background: phase >= 4 ? '#10B981' : '#7C3AED',
                    width: phase === 0 ? '0%' : phase <= 2 ? '50%' : phase <= 4 ? '70%' : '100%',
                    transition: 'width 0.6s ease, background 0.3s ease',
                }} />
            </div>

            {/* Traveling comment card */}
            <div style={{
                position: 'absolute',
                top: 90,
                left: cardLeft,
                width: '52%',
                maxWidth: 180,
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 10,
                padding: '10px 14px',
                opacity: phase === 0 ? 0 : 1,
                transition: 'left 0.6s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease',
                backdropFilter: 'blur(4px)',
            }}>
                {/* AI draft badge */}
                <div style={{
                    display: 'inline-block', fontSize: 9, fontWeight: 700,
                    color: '#C4B5FD', background: 'rgba(124,58,237,0.2)',
                    padding: '2px 8px', borderRadius: 4, marginBottom: 6,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                    AI Draft
                </div>
                <div style={{ fontSize: 11, color: '#fff', lineHeight: 1.5 }}>
                    "Your photography skills are incredible!"
                </div>

                {/* Scan line */}
                {phase === 3 && (
                    <div className="mission-scan-line" style={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: 3,
                        background: 'linear-gradient(90deg, transparent, #7C3AED, transparent)',
                        borderRadius: 2,
                    }} />
                )}

                {/* Tap indicator */}
                {phase === 4 && (
                    <div style={{
                        position: 'absolute', top: -16, right: -8,
                    }}>
                        <div className="mission-tap-press" style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: 'rgba(16,185,129,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                                <polyline points="20,6 9,17 4,12" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>

            {/* Celebration burst particles */}
            {phase === 5 && (
                <div style={{ position: 'absolute', top: 80, right: '10%' }}>
                    {[0, 1, 2, 3, 4].map(i => (
                        <div key={i} className="mission-burst-particle" style={{
                            position: 'absolute',
                            width: 4, height: 4, borderRadius: '50%',
                            background: i % 2 === 0 ? '#10B981' : '#C4B5FD',
                            animationDelay: `${i * 0.08}s`,
                        }} />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Main Section Component ─────────────────────────────────────────

const pillars = [
    {
        title: 'Replace Likes with Words',
        tagline: 'A heart tap takes zero thought. A real compliment can change someone\'s day.',
        Animation: PillarOneAnimation,
    },
    {
        title: 'Spread Positivity, Grow Together',
        tagline: 'Every comment lifts someone up — and brings you genuine connections in return.',
        Animation: PillarTwoAnimation,
    },
    {
        title: 'Human-Approved, Always',
        tagline: 'AI writes the first draft. You decide what goes out. Nothing is posted without your say.',
        Animation: PillarThreeAnimation,
    },
];

const SocialImpactSection: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); else setIsVisible(false); },
            { threshold: 0.15 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
                padding: '120px 24px',
            }}
        >
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                {/* Section Header */}
                <div style={{ textAlign: 'center', marginBottom: 80 }}>
                    <div style={{
                        display: 'inline-block',
                        padding: '6px 16px',
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 100,
                        fontSize: 12, fontWeight: 600,
                        color: '#FFFFFF',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        marginBottom: 24,
                    }}>
                        Our Mission
                    </div>

                    <h2 style={{
                        fontSize: 'clamp(32px, 5vw, 48px)',
                        fontWeight: 500,
                        color: '#FFFFFF',
                        marginBottom: 24,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                    }}>
                        More than just growth
                    </h2>

                    <p style={{
                        fontSize: 18,
                        color: 'rgba(255,255,255,0.85)',
                        lineHeight: 1.7,
                        maxWidth: 600,
                        margin: '0 auto',
                    }}>
                        We're replacing empty likes and generic emojis with genuine, thoughtful compliments.
                        Every comment you approve spreads positive energy to someone's day.
                    </p>
                </div>

                {/* Pillar Rows */}
                {pillars.map((pillar, idx) => {
                    const isReversed = idx % 2 === 1;
                    return (
                        <div
                            key={idx}
                            className={isReversed ? 'mission-pillar-row mission-pillar-row-reverse' : 'mission-pillar-row'}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 60,
                                padding: '60px 0',
                                ...(idx < pillars.length - 1 ? { borderBottom: '1px solid rgba(255,255,255,0.1)' } : {}),
                                ...(isReversed ? { flexDirection: 'row-reverse' } : {}),
                            }}
                        >
                            {/* Text Column */}
                            <div style={{ flex: '1 1 350px', minWidth: 280 }}>
                                <div style={{
                                    display: 'inline-block',
                                    fontSize: 11, fontWeight: 700,
                                    color: '#C4B5FD',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    marginBottom: 12,
                                }}>
                                    0{idx + 1}
                                </div>
                                <h3 style={{
                                    fontSize: 'clamp(24px, 3vw, 32px)',
                                    fontWeight: 600,
                                    color: '#FFFFFF',
                                    marginBottom: 16,
                                    lineHeight: 1.2,
                                }}>
                                    {pillar.title}
                                </h3>
                                <p style={{
                                    fontSize: 16,
                                    color: 'rgba(255,255,255,0.8)',
                                    lineHeight: 1.7,
                                    margin: 0,
                                }}>
                                    {pillar.tagline}
                                </p>
                            </div>

                            {/* Animation Column */}
                            <div
                                className="mission-animation-wrapper"
                                style={{
                                    flex: '0 0 360px',
                                    borderRadius: 16,
                                    background: 'rgba(255,255,255,0.08)',
                                    padding: 32,
                                }}
                            >
                                <pillar.Animation isVisible={isVisible} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Keyframes & Responsive Styles */}
            <style>{`
                @keyframes mission-heart-pop {
                    0% { transform: scale(1); }
                    40% { transform: scale(1.4); }
                    60% { transform: scale(0.9); }
                    100% { transform: scale(1); }
                }
                .mission-heart-pop { animation: mission-heart-pop 0.5s ease !important; }

                @keyframes mission-heart-shake {
                    0%, 100% { transform: translateX(0); }
                    20% { transform: translateX(-3px); }
                    40% { transform: translateX(3px); }
                    60% { transform: translateX(-2px); }
                    80% { transform: translateX(2px); }
                }
                .mission-heart-shake { animation: mission-heart-shake 0.5s ease !important; }

                @keyframes mission-ripple-expand {
                    0% { box-shadow: 0 0 0 0 rgba(124,58,237,0.5); }
                    100% { box-shadow: 0 0 0 12px rgba(124,58,237,0); }
                }
                .mission-ripple { animation: mission-ripple-expand 0.8s ease-out; }

                @keyframes mission-ai-sparkle {
                    0% { transform: scale(1) rotate(0deg); }
                    50% { transform: scale(1.2) rotate(15deg); }
                    100% { transform: scale(1) rotate(0deg); }
                }

                @keyframes mission-scan-line {
                    0% { top: 0; }
                    100% { top: 100%; }
                }
                .mission-scan-line { animation: mission-scan-line 0.8s ease-in-out; }

                @keyframes mission-tap-press {
                    0% { transform: scale(1); opacity: 0.6; }
                    50% { transform: scale(0.85); opacity: 1; }
                    100% { transform: scale(1); opacity: 0.6; }
                }
                .mission-tap-press { animation: mission-tap-press 0.6s ease; }

                @keyframes mission-celebration {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.15); }
                    100% { transform: scale(1); }
                }

                @keyframes mission-burst {
                    0% { transform: translate(0,0) scale(1); opacity: 1; }
                    100% { transform: translate(var(--bx), var(--by)) scale(0); opacity: 0; }
                }
                .mission-burst-particle:nth-child(1) { --bx: -16px; --by: -20px; }
                .mission-burst-particle:nth-child(2) { --bx: 18px; --by: -14px; }
                .mission-burst-particle:nth-child(3) { --bx: -10px; --by: 18px; }
                .mission-burst-particle:nth-child(4) { --bx: 20px; --by: 10px; }
                .mission-burst-particle:nth-child(5) { --bx: 0px; --by: -24px; }
                .mission-burst-particle { animation: mission-burst 0.6s ease-out forwards; }

                @media (max-width: 768px) {
                    .mission-pillar-row {
                        flex-direction: column !important;
                        gap: 32px !important;
                        padding: 40px 0 !important;
                        text-align: center;
                    }
                    .mission-pillar-row-reverse {
                        flex-direction: column !important;
                    }
                    .mission-animation-wrapper {
                        flex: unset !important;
                        width: 100% !important;
                        max-width: 320px !important;
                        margin: 0 auto !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default SocialImpactSection;
